import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type { ScanProgressExtended } from '../types';

/**
 * T026: useScanStatus hook (extended).
 * Polls GET /api/scan/:jobId/progress every 5s during active scan (FR-015a).
 * Handles ScanProgressExtended shape with ETA and scan type.
 * Invalidates store queries on completion.
 */
export function useScanStatus(jobId: string | null) {
  const queryClient = useQueryClient();

  return useQuery<ScanProgressExtended>({
    queryKey: ['scan-progress', jobId],
    queryFn: () => api.get<ScanProgressExtended>(`/scan/${jobId}/progress`),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 3000;

      // Stop polling on terminal states
      if (
        data.status === 'COMPLETED' ||
        data.status === 'FAILED' ||
        data.status === 'CANCELLED'
      ) {
        // Invalidate related queries so dashboard picks up new data
        void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
        void queryClient.invalidateQueries({ queryKey: ['store-score'] });
        return false;
      }

      return 3000; // Poll every 3 seconds for responsive progress updates
    },
  });
}
